import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Cinematic Ken Burns cross-fading image slideshow.
 * Simulates a slow, filmic video loop for hero sections without external video dependencies.
 *
 * Each slide has its own start/end transform (translate + scale) applied over `duration` ms,
 * then cross-fades into the next slide.
 */
export const KenBurns = ({
  images = [],
  duration = 8000,
  className = "",
}) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, duration);
    return () => clearInterval(t);
  }, [images.length, duration]);

  const variants = [
    { from: { scale: 1.06, x: "0%", y: "0%" }, to: { scale: 1.24, x: "-3.5%", y: "-2.2%" } },
    { from: { scale: 1.24, x: "-3%", y: "2%" }, to: { scale: 1.06, x: "2.5%", y: "-1.2%" } },
    { from: { scale: 1.08, x: "2%", y: "-1%" }, to: { scale: 1.28, x: "-2.5%", y: "1.5%" } },
    { from: { scale: 1.22, x: "-1%", y: "-2%" }, to: { scale: 1.06, x: "1.5%", y: "2.2%" } },
    { from: { scale: 1.10, x: "1%", y: "1%" }, to: { scale: 1.26, x: "-1.5%", y: "-1.8%" } },
  ];

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <AnimatePresence>
        {images.map((src, i) =>
          i === idx ? (
            <motion.div
              key={src + i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.2, ease: [0.65, 0, 0.15, 1] }}
              className="absolute inset-0"
            >
              <motion.div
                initial={variants[i % variants.length].from}
                animate={variants[i % variants.length].to}
                transition={{
                  duration: (duration + 2600) / 1000,
                  ease: "linear",
                }}
                className="absolute inset-0 w-full h-full bg-center bg-cover"
                style={{ backgroundImage: `url(${src})` }}
              />
            </motion.div>
          ) : null
        )}
      </AnimatePresence>
    </div>
  );
};
